import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom'

// import once for the entire app --> not true, I had to reimport it in PdfReceipt
import 'dayjs/locale/fr'

// Supabase
import { createClient } from '@supabase/supabase-js';

import Account from "./pages/Account/Account"
import Companies from "./pages/Companies/Companies"
import Dashboard from "./pages/Dashboard/Dashboard"
import Leases from "./pages/Leases/Leases"
import Payments from "./pages/Payments/Payments"
import People from "./pages/People/People"
import Properties from "./pages/Properties/Properties"
import Property from "./pages/Property/Property"
import DPE from "./pages/DPE/DPE"
import SignInSide from "./pages/Authentication/SignInSide"

import {
    chartsCustomizations,
    dataGridCustomizations,
    datePickersCustomizations,
    treeViewCustomizations,
} from './theme/customizations'
import ThemeSettings from "./theme/ThemeSettings"
import CssBaseline from "@mui/material/CssBaseline"
import Box from "@mui/material/Box"
import SideMenu from "./components/SideMenu"
import { alpha } from "@mui/material/styles"
import AppTheme from "./AppTheme"


const supabase = createClient(
    process.env.REACT_APP_SUPABASE_URL,
    process.env.REACT_APP_SUPABASE_ANON_KEY
);

const xThemeComponents = {
    ...chartsCustomizations,
    ...dataGridCustomizations,
    ...datePickersCustomizations,
    ...treeViewCustomizations,
}

const App = (props) => {

    const colorScheme = 'light'
    const navigate = useNavigate()
    const [session, setSession] = useState(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session)
            setIsLoading(false)
        })

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session)
        })

        return () => subscription.unsubscribe()
    }, [])

    // Does not render during initialization of supabase.auth, otherwise we jump pages during the process
    if (isLoading) return null

    if (session) {
        return (
            <AppTheme {...props} themeComponents={xThemeComponents}>
                <CssBaseline enableColorScheme />
                <ThemeSettings modeName={colorScheme}/>
                <Box sx={{ display: 'flex' }}>
                    <SideMenu
                        signOut={() => supabase.auth.signOut()}
                        user={session?.user}
                    />
                    <Box
                        component="main"
                        sx={(theme) => ({
                            flexGrow: 1,
                            backgroundColor: theme.vars
                                ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                                : alpha(theme.palette.background.default, 1),
                            margin: 4
                        })}
                    >
                        <Routes>
                            <Route path="/account" element={<Account supabase={supabase} /> } />
                            <Route path="/authentication" element={<Navigate to="/" replace /> } />
                            <Route path="/" element={<Dashboard supabase={supabase} /> } />
                            <Route path="/people" element={<People supabase={supabase} /> } />
                            <Route path="/companies" element={<Companies supabase={supabase} /> } />
                            <Route path="/dpe" element={<DPE supabase={supabase} /> } />
                            <Route path="/properties" element={<Properties supabase={supabase} /> } />
                            <Route path="/property/:propertyId" element={<Property supabase={supabase} /> } />
                            <Route path="/leases" element={<Leases supabase={supabase} /> } />
                            <Route path="/payments" element={<Payments supabase={supabase} /> } />
                            <Route path="/parameters" element={<></>} />
                            <Route path="/feedbacks" element={<></>} />
                            <Route path="/about" element={<></>} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Box>
                </Box>
            </AppTheme>
        )
    } else {
        return (
            <AppTheme {...props} themeComponents={xThemeComponents}>
                <CssBaseline enableColorScheme />
                <ThemeSettings modeName={colorScheme}/>
                <Box sx={{ display: 'flex' }}>
                    <Box
                        component="main"
                        sx={(theme) => ({
                            flexGrow: 1,
                            backgroundColor: theme.vars
                                ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                                : alpha(theme.palette.background.default, 1),
                            margin: 0
                        })}
                    >
                        <Routes>
                            <Route path="/auth/confirm" element={
                                (() => {
                                    function Confirm() {
                                        const [searchParams] = useSearchParams();
                                        const token_hash = searchParams.get('token_hash');
                                        const type = searchParams.get('type');

                                        useEffect(() => {
                                            async function verify() {
                                                const { error } = await supabase.auth.verifyOtp({ token_hash, type });
                                                if (error) {
                                                    console.error('OTP verification error:', error);
                                                }else {
                                                    navigate('/')
                                                }
                                            }
                                            verify();
                                        }, [token_hash, type]);

                                        return (
                                            <div>
                                                <h2>Confirmation</h2>
                                                <p>Verifying your magic link...</p>
                                            </div>
                                        );
                                    }
                                    return <Confirm />;
                                })()
                            } />
                            <Route path="/authentication" element={
                                <SignInSide
                                    signInWithEmail={async (email) => supabase.auth.signInWithOtp({
                                        email: email,
                                        options: {
                                            // set this to false if you do not want the user to be automatically signed up
                                            shouldCreateUser: false,
                                            emailRedirectTo: 'https://gelo.masiak.fr/',
                                        },
                                    })}
                                />
                            } />
                            <Route path="*" element={<Navigate to="/authentication" replace />} />
                        </Routes>
                    </Box>
                </Box>
            </AppTheme>
        )
    }
}

export default App
